import { useEffect, useState } from "react";

export type ColorScheme = "light" | "dark";

export const COLOR_SCHEME_STORAGE_KEY = "theme";

export const COLOR_SCHEME_CHANGE_EVENT = "ani-color-scheme-change";

export function getStoredColorScheme(): ColorScheme {
  if (typeof window === "undefined") return "dark";
  return localStorage.getItem(COLOR_SCHEME_STORAGE_KEY) === "light" ? "light" : "dark";
}

export function getIsDarkFromDocument(): boolean {
  if (typeof window === "undefined") return true;
  return document.documentElement.classList.contains("dark");
}

export function applyColorScheme(colorScheme: ColorScheme): void {
  if (typeof window === "undefined") return;
  document.documentElement.classList.toggle("dark", colorScheme === "dark");
  localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, colorScheme);
  window.dispatchEvent(
    new CustomEvent(COLOR_SCHEME_CHANGE_EVENT, { detail: colorScheme }),
  );
}

export function subscribeColorScheme(
  callback: (colorScheme: ColorScheme) => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onEvent = () => callback(getStoredColorScheme());
  window.addEventListener(COLOR_SCHEME_CHANGE_EVENT, onEvent);
  const observer = new MutationObserver(() => {
    callback(getIsDarkFromDocument() ? "dark" : "light");
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => {
    window.removeEventListener(COLOR_SCHEME_CHANGE_EVENT, onEvent);
    observer.disconnect();
  };
}

export function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() =>
    typeof window === "undefined" ? true : getIsDarkFromDocument(),
  );
  useEffect(() => subscribeColorScheme((scheme) => setIsDark(scheme === "dark")), []);
  return isDark;
}
